const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtPct = (n, digits = 1) => `${Number(n ?? 0).toFixed(digits)}%`;

const sortBlocks = (blockData = []) =>
  [...(blockData || [])].sort((a, b) => (b.total_teachers || 0) - (a.total_teachers || 0));

export const buildInsightReadinessScore = (overview = {}) => {
  const aadhaar = overview.aadhaar_verified_pct ?? 0;
  const completion = overview.completion_pct ?? 0;
  const ctet = overview.ctet_pct ?? 0;
  const nishtha = overview.nishtha_pct ?? 0;
  let score = aadhaar * 0.3 + completion * 0.4 + ctet * 0.1 + nishtha * 0.2;
  if (aadhaar >= 90 && completion >= 95) score += 8;
  return Math.min(99, Math.max(0, Math.round(score)));
};

export const buildInsightGaugeStyle = (score = 0) => {
  const pct = Math.min(100, Math.max(0, Number(score) || 0));
  return {
    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--ctin-purple) 0 ${pct}%, rgba(139,92,246,.12) ${pct}% 100%)`,
  };
};

export const buildInsightHeroNarrative = (overview = {}) => {
  const completion = overview.completion_pct ?? 0;
  const aadhaar = overview.aadhaar_verified_pct ?? 0;
  const ctet = overview.ctet_pct ?? 0;
  const nishtha = overview.nishtha_pct ?? 0;
  if (aadhaar >= 90 && completion >= 90) {
    return `Pune has strong data completion and Aadhaar verification, but low CTET qualification and NISHTHA completion indicate the primary workforce-quality gap.`;
  }
  return `Data completion is at ${fmtPct(completion)} and Aadhaar verification at ${fmtPct(aadhaar)}. CTET (${fmtPct(ctet)}) and NISHTHA (${fmtPct(nishtha)}) remain the priority improvement areas.`;
};

export const buildInsightPrimaryNote = (overview = {}) => {
  const completion = overview.completion_pct ?? 0;
  const aadhaar = overview.aadhaar_verified_pct ?? 0;
  if (aadhaar >= 90 && completion >= 95) {
    return "Teacher data is reliable enough for block-level action. The focus should shift from data collection to certification closure and low-volume block support.";
  }
  if (completion < 90) {
    return "Complete pending teacher records first, then shift focus to certification closure and block-level staffing validation.";
  }
  return "Strengthen identity verification outliers, then prioritize certification closure and low-volume block support.";
};

export const buildInsightHeroKpis = (overview = {}) => {
  const total = overview.total_teachers ?? 0;
  const records = overview.teacher_records ?? overview.unique_teachers ?? 0;
  const ctet = overview.ctet_pct ?? 0;
  const nishtha = overview.nishtha_pct ?? 0;
  const blocks = overview.total_blocks ?? 0;
  return [
    {
      label: "Total Teachers",
      value: fmt(total),
      sub: records && records !== total ? `${fmt(records)} roster records` : `${fmt(overview.total_schools)} schools`,
      cardClass: "",
    },
    {
      label: "Schools Covered",
      value: fmt(overview.total_schools),
      sub: blocks > 0 ? `${blocks} blocks monitored` : "District coverage",
      cardClass: "",
    },
    {
      label: "CTET Qualified",
      value: fmtPct(ctet),
      sub: ctet < 15 ? "Low qualification signal" : ctet < 50 ? "Qualification gap" : "On track",
      cardClass: "danger",
    },
    {
      label: "NISHTHA Completed",
      value: fmtPct(nishtha),
      sub: nishtha < 50 ? "Training closure gap" : nishtha < 80 ? "Needs follow-up" : "Training on track",
      cardClass: "warn",
    },
  ];
};

export const buildInsightDomains = (overview = {}, blockData = []) => {
  const completion = overview.completion_pct ?? 0;
  const aadhaar = overview.aadhaar_verified_pct ?? 0;
  const ctet = overview.ctet_pct ?? 0;
  const notCtet = Math.min(100, Math.max(0, 100 - ctet));

  const blocks = sortBlocks(blockData);
  const lowest = blocks.length ? blocks[blocks.length - 1] : null;
  const lowestCount = lowest?.total_teachers ?? 0;
  const highest = blocks[0];
  const highestCount = highest?.total_teachers ?? 1;
  const blockBarPct = highestCount > 0 ? Math.min(100, Math.round((lowestCount / highestCount) * 100)) : 38;

  return [
    {
      title: "Data Reliability",
      score: fmtPct(completion),
      scoreLabel: "Completion",
      barPct: completion,
      barColor: "var(--ctin-accent)",
      iconClass: "green",
      dotClass: "",
      metaLeft: completion >= 95 ? "Ready for action" : "Needs cleanup",
      metaRight: completion >= 95 ? "Low missingness" : "Complete records",
    },
    {
      title: "Identity Confidence",
      score: fmtPct(aadhaar),
      scoreLabel: "Aadhaar",
      barPct: aadhaar,
      barColor: "var(--ctin-primary)",
      iconClass: "",
      dotClass: "",
      metaLeft: aadhaar >= 90 ? "Strong base" : "Verification push",
      metaRight: "Validate outliers",
    },
    {
      title: "Certification Gap",
      score: fmtPct(notCtet),
      scoreLabel: "Not CTET",
      barPct: notCtet,
      barColor: "var(--ctin-danger)",
      iconClass: "red",
      dotClass: "red",
      metaLeft: "Highest risk",
      metaRight: "Needs sprint",
    },
    {
      title: "Block Variation",
      score: fmt(lowestCount),
      scoreLabel: "Lowest block",
      barPct: blockBarPct,
      barColor: "var(--ctin-secondary)",
      iconClass: "orange",
      dotClass: "amber",
      metaLeft: lowest ? `${lowest.block_name} focus` : "Review blocks",
      metaRight: "Resource review",
    },
  ];
};

export const buildRootCauseSignals = (overview = {}, blockData = []) => {
  const completion = overview.completion_pct ?? 0;
  const aadhaar = overview.aadhaar_verified_pct ?? 0;
  const ctet = overview.ctet_pct ?? 0;
  const nishtha = overview.nishtha_pct ?? 0;

  const blocks = sortBlocks(blockData);
  const top = blocks[0];
  const bottom = blocks.length ? blocks[blocks.length - 1] : null;

  return [
    {
      title: "Data Foundation Is Strong",
      description: `Completion at ${fmtPct(completion)} and Aadhaar verification at ${fmtPct(aadhaar)} make the data usable for operational review.`,
      impact: "High",
      iconClass: "green",
    },
    {
      title: "CTET Qualification Is the Biggest Gap",
      description: `Only ${fmtPct(ctet)} are CTET qualified, creating a district-wide qualification and compliance risk.`,
      impact: ctet < 10 ? "Critical" : "High",
      iconClass: "red",
    },
    {
      title: "NISHTHA Completion Is Lagging",
      description: `Training completion at ${fmtPct(nishtha)} suggests follow-up is needed after enrolment or assignment.`,
      impact: nishtha < 50 ? "High" : "Medium",
      iconClass: "orange",
    },
    {
      title: "Block Concentration Needs Balancing",
      description: top && bottom
        ? `${top.block_name} performs strongly, while ${bottom.block_name} has a small teacher base and needs targeted resource validation.`
        : "Block-level teacher distribution shows concentration gaps that need targeted resource validation.",
      impact: "Medium",
      iconClass: "purple",
    },
  ];
};

export const buildRecommendationStrength = (overview = {}) => {
  const ctet = overview.ctet_pct ?? 0;
  const nishtha = overview.nishtha_pct ?? 0;
  const aadhaar = overview.aadhaar_verified_pct ?? 0;
  const completion = overview.completion_pct ?? 0;

  const clamp = (n) => Math.min(99, Math.max(20, Math.round(n)));

  return [
    {
      label: "CTET Closure\nsprint",
      value: clamp(90 + (100 - ctet) / 15),
      barClass: "red",
    },
    {
      label: "Nishtha\nfollow-Up",
      value: clamp(50 + (100 - nishtha) * 0.45),
      barClass: "orange",
    },
    {
      label: "Block Resource\nreview",
      value: clamp(55 + (100 - ctet) * 0.12),
      barClass: "purple",
    },
    {
      label: "Verification\nexception Audit",
      value: clamp(35 + (100 - aadhaar) * 0.65),
      barClass: "",
    },
    {
      label: "Data Quality\nrefresh",
      value: clamp(25 + (100 - completion) * 0.55),
      barClass: "green",
    },
  ];
};

export const buildInsightWatchlist = (overview = {}, blockData = []) => {
  const blocks = sortBlocks(blockData);
  const top = blocks[0];
  const bottom = blocks.length ? blocks[blocks.length - 1] : null;
  const ctet = overview.ctet_pct ?? 0;
  const nishtha = overview.nishtha_pct ?? 0;
  const completion = overview.completion_pct ?? 0;

  const rows = [];

  if (bottom) {
    rows.push({
      priority: 1,
      block: (bottom.block_name || "—").toUpperCase(),
      signal: "Low teacher base",
      evidence: fmt(bottom.total_teachers),
      move: "Validate deployment and vacancies",
      status: "Critical",
      tagClass: "red",
    });
  }

  rows.push({
    priority: rows.length + 1,
    block: "District",
    signal: "Low CTET qualification",
    evidence: fmtPct(ctet),
    move: "Start qualification sprint",
    status: "Critical",
    tagClass: "red",
  });

  rows.push({
    priority: rows.length + 1,
    block: "District",
    signal: "NISHTHA completion gap",
    evidence: fmtPct(nishtha),
    move: "Weekly follow-up list",
    status: "Amber",
    tagClass: "",
  });

  if (top) {
    rows.push({
      priority: rows.length + 1,
      block: (top.block_name || "—").toUpperCase(),
      signal: "Top performance",
      evidence: fmt(top.total_teachers),
      move: "Replicate practice model",
      status: "Benchmark",
      tagClass: "green",
    });
  }

  rows.push({
    priority: rows.length + 1,
    block: "Data Team",
    signal: "Data completion",
    evidence: fmtPct(completion),
    move: "Maintain monthly audit",
    status: "Stable",
    tagClass: "green",
  });

  return rows;
};

export const buildInsightActions = (overview = {}, blockData = []) => {
  const blocks = sortBlocks(blockData);
  const top = blocks[0];
  const bottom = blocks.length ? blocks[blocks.length - 1] : null;

  return [
    {
      title: "Launch CTET Qualification Closure Sprint",
      description:
        "Create a block-wise list of non-qualified teachers and monitor weekly progress with BEO-level ownership.",
      iconClass: "red",
    },
    {
      title: "Close NISHTHA Completion Backlog",
      description:
        "Segment pending teachers by school, assign reminders and publish a completion heatmap.",
      iconClass: "orange",
    },
    {
      title: "Review Low-Base and High-Performing Blocks",
      description: bottom && top
        ? `Validate ${bottom.block_name} staffing requirements and document ${top.block_name} practices for replication.`
        : "Validate low-base block staffing and document high-performing block practices for replication.",
      iconClass: "purple",
    },
  ];
};

export const buildCTTeacherInsightsMarkdown = (overview = {}, blockData = []) => {
  const signals = buildRootCauseSignals(overview, blockData);
  const watchlist = buildInsightWatchlist(overview, blockData);
  const actions = buildInsightActions(overview, blockData);
  return [
    "## Insights",
    `- ${buildInsightHeroNarrative(overview)}`,
    `- ${buildInsightPrimaryNote(overview)}`,
    ...watchlist.slice(0, 3).map((w) => `- ${w.block}: ${w.signal}`),
    "",
    "## Root Cause Signals",
    ...signals.map((s) => `- ${s.title}: ${s.description}`),
    "",
    "## Recommendations",
    ...buildRecommendationStrength(overview).map((r) => `- ${r.label}: ${r.value}`),
    "",
    "## Priority Action Items",
    ...actions.map((a) => `- ${a.title}: ${a.description}`),
  ].join("\n");
};
